import type { Breadcrumb, ErrorEvent } from '@sentry/vue';
import { isCredentialKey, objectKeyPaths, redactHeaders, redactUrl, redactValue, truncate } from './redact';
import type { TrailEntry } from './trail';
import type { NormalizedError } from './errorTypes';

/**
 * The debugging payload.
 *
 * Target: *a support engineer reading this alone can reproduce the issue without asking the user
 * anything.* Identifiers, route params, study config values and the action trail are therefore kept
 * in full — they are surrogate keys and configuration, not subject data. Only credentials and
 * typed-in content are withheld (see `redact.ts`).
 *
 * This is not shown to the user. The dialog shows a sentence and a reference; `messages.ts` builds the
 * short "Show more" view. Keeping the two formatters separate means "make the UI more informative"
 * cannot quietly widen into "render the whole payload".
 */

/** Everything the report needs from the outside world, passed in so this module stays pure. */
export interface DiagnosticContext {
  release: string;
  commit: string;
  environment: string;
  route?: {
    name?: string;
    pathTemplate?: string;
    path?: string;
    params?: Record<string, string>;
    query?: Record<string, string>;
  };
  userIdentifier?: string;
  userEmail?: string;
  organizationIdentifier?: string;
  studyIdentifier?: string;
  studyB64Identifier?: string;
  studyLongitudinal?: boolean;
  studyFlags?: Record<string, unknown>;
  caseIdentifier?: string;
  entryIdentifier?: string;
  formIdentifier?: string;
  trail?: TrailEntry[];
  occurrences?: number;
  replayId?: string;
}

export interface DiagnosticReport {
  reference: string;
  occurredAt: string;
  kind: string;
  fingerprint: string;
  occurrences: number;
  release: string;
  commit: string;
  environment: string;
  errorName?: string;
  errorMessage?: string;
  serverMessage?: string;
  stack?: string;
  code?: string;
  http?: {
    method?: string;
    status?: number;
    path?: string;
    params?: Record<string, string>;
    bodyKeys?: string[];
    validationFields?: string[];
    requestId?: string;
    sentryTrace?: string;
    durationMs?: number;
  };
  route?: DiagnosticContext['route'];
  context: {
    studyIdentifier?: string;
    studyB64Identifier?: string;
    studyLongitudinal?: boolean;
    studyFlags?: Record<string, unknown>;
    caseIdentifier?: string;
    entryIdentifier?: string;
    formIdentifier?: string;
  };
  user: {
    identifier?: string;
    email?: string;
    organizationIdentifier?: string;
  };
  client: {
    userAgent?: string;
    viewport?: string;
    online?: boolean;
    language?: string;
    timezone?: string;
  };
  trail: TrailEntry[];
  sentryEventId?: string;
  replayId?: string;
}

/** Frames are the most useful field in the whole report; the caps only exist to bound payload size. */
const STACK_FRAME_LIMIT = 30;
const STACK_CHAR_LIMIT = 4000;

const trimStack = (stack?: string): string | undefined => {
  if (!stack) {
    return undefined;
  }
  return truncate(stack.split('\n').slice(0, STACK_FRAME_LIMIT).join('\n'), STACK_CHAR_LIMIT);
};

const readClient = (): DiagnosticReport['client'] => {
  try {
    return {
      userAgent: truncate(navigator.userAgent, 300),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      online: navigator.onLine,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  } catch {
    return {};
  }
};

export const buildDiagnosticReport = (error: NormalizedError, ctx: DiagnosticContext): DiagnosticReport => {
  const cause = error.cause;

  return {
    reference: error.reference,
    occurredAt: error.occurredAt,
    kind: error.kind,
    fingerprint: error.fingerprint,
    occurrences: ctx.occurrences ?? 1,
    release: ctx.release,
    commit: ctx.commit,
    environment: ctx.environment,
    errorName: cause instanceof Error ? cause.name : typeof cause,
    errorMessage: truncate(cause instanceof Error ? cause.message : String(cause ?? ''), 300),
    serverMessage: error.serverMessage,
    stack: trimStack(cause instanceof Error ? cause.stack : undefined),
    code: error.code,
    http: error.request ? {
      method: error.request.method,
      status: error.httpStatus,
      path: error.request.path,
      params: error.request.params,
      bodyKeys: error.request.bodyKeys,
      validationFields: error.request.validationFields,
      requestId: error.request.requestId,
      sentryTrace: error.request.sentryTrace,
      durationMs: error.request.durationMs
    } : undefined,
    route: ctx.route,
    context: {
      studyIdentifier: ctx.studyIdentifier,
      studyB64Identifier: ctx.studyB64Identifier,
      studyLongitudinal: ctx.studyLongitudinal,
      // Values, not just keys: cfrPart11 and longitudinal change code paths, so a report without
      // them cannot be reproduced. This is study configuration, not subject data.
      studyFlags: ctx.studyFlags,
      caseIdentifier: ctx.caseIdentifier,
      entryIdentifier: ctx.entryIdentifier,
      formIdentifier: ctx.formIdentifier
    },
    user: {
      identifier: ctx.userIdentifier,
      email: ctx.userEmail,
      organizationIdentifier: ctx.organizationIdentifier
    },
    client: readClient(),
    trail: ctx.trail ?? [],
    sentryEventId: error.sentryEventId,
    replayId: ctx.replayId
  };
};

const line = (label: string, value: unknown): string | undefined => {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return undefined;
  }
  const rendered = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return `${label}: ${rendered}`;
};

const formatTrailEntry = (entry: TrailEntry): string =>
  `  ${entry.at} ${entry.type} ${entry.label}${entry.status ? ` -> ${entry.status}` : ''}`;

/**
 * The full report as plain text, for the email body and the clipboard.
 *
 * Deliberately flat and label-per-line rather than JSON: a support engineer reads this in a mail
 * client, and a wall of JSON is where useful fields go to hide.
 */
export const formatDiagnosticReport = (report: DiagnosticReport, note?: string): string => {
  const sections: (string | undefined)[] = [
    `Studypages Data — error report ${report.reference}`,
    '',
    note ? `What the user was doing:\n${note}\n` : undefined,
    line('Reference', report.reference),
    line('When', report.occurredAt),
    line('Kind', report.kind),
    line('Occurrences', report.occurrences),
    line('App version', `${report.release} (${report.commit})`),
    line('Environment', report.environment),
    '',
    line('Error', [report.errorName, report.errorMessage].filter(Boolean).join(': ')),
    line('Server said', report.serverMessage),
    line('Error code', report.code),
    '',
    line('Request', report.http ?
      `${report.http.method ?? '?'} ${report.http.path ?? '?'} -> ${report.http.status ?? 'no response'}` :
      undefined),
    line('Request params', report.http?.params),
    line('Payload fields', report.http?.bodyKeys),
    line('Invalid fields', report.http?.validationFields),
    line('Request id', report.http?.requestId),
    line('Trace', report.http?.sentryTrace),
    '',
    line('Screen', report.route?.name),
    line('Route', report.route?.pathTemplate),
    line('Path', report.route?.path),
    line('Route params', report.route?.params),
    line('Query', report.route?.query),
    '',
    line('Study', report.context.studyIdentifier ?? report.context.studyB64Identifier),
    line('Longitudinal', report.context.studyLongitudinal),
    line('Study flags', report.context.studyFlags),
    line('Case', report.context.caseIdentifier),
    line('Entry', report.context.entryIdentifier),
    line('Form', report.context.formIdentifier),
    '',
    line('User', report.user.identifier),
    line('Email', report.user.email),
    line('Organization', report.user.organizationIdentifier),
    '',
    line('Browser', report.client.userAgent),
    line('Viewport', report.client.viewport),
    line('Online', report.client.online),
    line('Language', report.client.language),
    line('Timezone', report.client.timezone),
    '',
    line('Sentry event', report.sentryEventId),
    line('Session replay', report.replayId),
    '',
    report.trail.length > 0 ? `Recent activity:\n${report.trail.map(formatTrailEntry).join('\n')}` : undefined,
    '',
    report.stack ? `Stack:\n${report.stack}` : undefined
  ];

  return sections.filter(section => section !== undefined).join('\n').replace(/\n{3,}/g, '\n\n');
};

const scrubBreadcrumb = (crumb: Breadcrumb): Breadcrumb => {
  const data = crumb.data as Record<string, unknown> | undefined;

  if (crumb.category === 'xhr' || crumb.category === 'fetch') {
    const url = redactUrl(typeof data?.url === 'string' ? data.url : undefined);
    return {
      ...crumb,
      // eslint-disable-next-line camelcase
      data: { method: data?.method, status_code: data?.status_code, url: url?.path, params: url?.params }
    };
  }
  if (crumb.category === 'navigation') {
    return {
      ...crumb,
      data: {
        from: redactUrl(typeof data?.from === 'string' ? data.from : undefined)?.path,
        to: redactUrl(typeof data?.to === 'string' ? data.to : undefined)?.path
      }
    };
  }
  if (crumb.category === 'console') {
    // Console arguments in this codebase include whole entry objects.
    return { ...crumb, message: truncate(crumb.message, 200), data: undefined };
  }
  if (crumb.category?.startsWith('ui.')) {
    // The message is a CSS selector, which is useful; `data` is not worth reasoning about.
    return { ...crumb, data: undefined };
  }
  return crumb;
};

/**
 * `beforeSend` — the only chokepoint an integration cannot bypass.
 *
 * We already gate at capture time, so this is defence in depth for events we did not author:
 * breadcrumbs, `httpContext`, anything a future integration attaches.
 */
export const scrubSentryEvent = (event: ErrorEvent): ErrorEvent | null => {
  try {
    if (event.tags?.['error.expected'] === 'true') {
      return null;
    }

    if (event.request) {
      const url = redactUrl(event.request.url);
      event.request.url = url?.path;
      // eslint-disable-next-line camelcase
      event.request.query_string = url?.params ? new URLSearchParams(url.params).toString() : undefined;
      // Field names survive, values do not — "the payload was missing timepoint" stays diagnosable.
      event.request.data = event.request.data ? objectKeyPaths(event.request.data) : undefined;
      event.request.cookies = undefined;
      event.request.env = undefined;
      event.request.headers = redactHeaders(event.request.headers);
    }

    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
    }

    if (event.contexts?.vue) {
      // `attachProps` is off, but props are CRF fields — belt and braces.
      delete (event.contexts.vue as Record<string, unknown>).propsData;
    }

    if (event.extra) {
      event.extra = redactValue(event.extra) as Record<string, unknown>;
    }
    if (event.contexts) {
      event.contexts = redactValue(event.contexts) as typeof event.contexts;
    }
    if (event.user) {
      event.user = { id: event.user.id, email: event.user.email };
    }

    return event;
  } catch {
    // A throwing `beforeSend` silently drops the event. Reporting something unscrubbed by a bug in
    // the scrubber beats reporting nothing, and the capture-time gating still applied.
    return event;
  }
};

/** Re-exported so callers scrubbing ad-hoc objects do not reach past this module. */
export { isCredentialKey };
